/**
 * ChatInput - Message input with attachment support
 */

import { useState, useRef, KeyboardEvent } from 'react'
import { Send, Paperclip, X, Loader2 } from 'lucide-react'
import { useChatAttachments } from '../../hooks/useChatAttachments'

interface ChatInputProps {
  onSendMessage: (message: string) => Promise<void>
  isSending: boolean
  disabled?: boolean
  bookingId: string
  threadId: string
}

export default function ChatInput({
  onSendMessage,
  isSending,
  disabled = false,
  bookingId,
  threadId
}: ChatInputProps) {
  const [messageText, setMessageText] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    sendMessageWithAttachment,
    isUploading,
    validateFile,
    error: attachmentError,
    reset: resetAttachment
  } = useChatAttachments(bookingId, threadId)

  const isDisabled = disabled || isSending || isUploading

  const handleSend = async () => {
    if (isDisabled) return

    // If there's a file, send with attachment
    if (selectedFile) {
      const result = await sendMessageWithAttachment(messageText || 'Attachment', selectedFile)
      if (result.success) {
        setMessageText('')
        setSelectedFile(null)
        resetAttachment()
      }
      return
    }

    // Otherwise send text message
    if (messageText.trim()) {
      await onSendMessage(messageText)
      setMessageText('')
    }
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const validation = validateFile(file)
    if (!validation.valid) {
      alert(validation.error)
      return
    }

    setSelectedFile(file)
    resetAttachment()
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    resetAttachment()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="border-t border-gray-200 bg-white p-3">
      {/* File preview */}
      {selectedFile && (
        <div className="mb-2 flex items-center gap-2 bg-gray-50 rounded-lg p-2">
          <Paperclip className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-700 flex-1 truncate">
            {selectedFile.name}
          </span>
          <button
            onClick={handleRemoveFile}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Remove attachment"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Error message */}
      {attachmentError && (
        <div className="mb-2 text-xs text-red-500 bg-red-50 rounded p-2">
          {attachmentError}
        </div>
      )}

      {/* Input area */}
      <div className="flex items-end gap-2">
        {/* File attachment button */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isDisabled || !!selectedFile}
          className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Attach file"
        >
          <Paperclip className="w-5 h-5" />
        </button>

        {/* Text input */}
        <textarea
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type a message..."
          disabled={isDisabled}
          rows={1}
          className="flex-1 resize-none border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed text-sm"
          style={{
            minHeight: '38px',
            maxHeight: '120px'
          }}
        />

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={isDisabled || (!messageText.trim() && !selectedFile)}
          className="flex-shrink-0 bg-primary hover:bg-primary-600 text-white p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Send message"
        >
          {isSending || isUploading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Help text */}
      <div className="mt-2 text-xs text-gray-400">
        Press Enter to send, Shift+Enter for new line
      </div>
    </div>
  )
}
